import { styled, css } from 'next-yak'

import * as breakpoints from '../../constants/breakpoints'
import * as button from '../button'
import * as colors from '../../constants/colors'
import * as copy from '../copy'
import * as effects from '../../constants/effects'
import * as glyphs from '../../icons/glyphs'
import * as heading from '../heading'
import * as wysiwyg from '../wysiwyg'
import { Tag } from '../tag'
import { Tooltip } from '../tooltip'
import type { PolymorphicComponent, YakComponentOf } from '@owl-nest/yak/extra-runtime'

type LockedProps = {
  $isLocked: boolean
}
type MediaTypeProps = {
  $type: 'image' | 'video'
}

type LockLabelProps = LockedProps & MediaTypeProps

export const NewsCardWrapper = styled.div`
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${effects.SHADOW_3};
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 760px;
  min-width: 288px;
  position: relative;
  width: 100%;

  ${button.EditButton} {
    position: absolute;
    right: 8px;
    top: 8px;
    z-index: 2;
  }
`

export const LockLabel = styled(copy.M.semiBold)<LockLabelProps>`
  display: ${(props) => (props.$isLocked ? 'flex' : 'none')};
  align-items: center;
  gap: 8px;
  color: ${colors.PRIMARY_GREY_000};
  user-select: none;
  z-index: 1;
  position: absolute;
  top: ${(props) => (props.$type === 'image' ? '50%' : '78%')};
  left: ${(props) => (props.$type === 'image' ? '50%' : '4%')};
  transform: ${(props) => (props.$type === 'image' ? 'translate(-50%, -50%)' : 'translate(0%, 0%)')};

  @media screen and ${breakpoints.MOBILE_L} {
    top: ${(props) => (props.$type === 'image' ? '50%' : '84%')};
  }
`

export const NewsCardMediaWrapper = styled.div<LockLabelProps>`
  min-height: 162px;
  aspect-ratio: 16/9;
  width: 100%;
  overflow: hidden;
  position: relative;

  ${(props) =>
    props.$isLocked &&
    props.$type === 'image' &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${colors.PRIMARY_GREY_900};
        opacity: 0.5;
      }
    `}
`

export const NewsCardImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
`

export const NewsCardContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`
export const NewsCardHeader = styled.div`
  justify-content: space-between;
  align-items: start;
  display: flex;
  width: 100%;
  flex-wrap: wrap;

  ${Tag} {
    display: flex;
    align-items: center;
    gap: 5px;
    position: relative;

    &:hover ${Tooltip} {
      display: block;
    }
  }

  ${glyphs.Icon} {
    top: 0px !important;
  }

  ${glyphs.stroke.Info} {
    color: ${colors.PRIMARY_SAND_500};
  }

  ul {
    margin: 0;
  }

  ${Tooltip} {
    max-width: 226px;
  }
`

export const NewsCardDate = styled(copy.XS.regular)`
  color: ${colors.TEXT_TERTIARY};
  flex-grow: 1;
  text-transform: uppercase;
`

const textBase = css`
  color: ${colors.PRIMARY_GREY_900};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  position: relative;
`

export const NewsCardTitle = styled(heading.XXS)`
  max-height: 50px;
  padding-bottom: 1.5px;
  -webkit-line-clamp: 2;
  ${textBase};

  @media screen and ${breakpoints.MOBILE_L} {
    max-height: 60px;
  }
`

export const NewsCardDescription = styled.div<LockedProps>`
  ${wysiwyg._paragraphStyle};
  ${textBase};
  -webkit-line-clamp: 3;
  height: fit-content;
  max-height: 80px;

  a {
    ${wysiwyg._hyperlinkStyle};
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ${(props) =>
    props.$isLocked &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
      }
    `};
`

export const NewsCardFooter = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
  width: 100%;
  gap: 16px;
  flex-wrap: wrap;

  @media screen and ${breakpoints.MOBILE_L} {
    gap: 20px;
  }
`

export const CommentsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;

  ${glyphs.Icon} {
    color: ${colors.TEXT_TERTIARY};
  }
`

export const CommentsCount = styled(copy.XS.regular)`
  min-width: 110px;
  color: ${colors.TEXT_TERTIARY};
  font-weight: 400; // HACK: Irregular font manipulation.
` as YakComponentOf<PolymorphicComponent<'p', copy.CopyProps>>

export const SubscriptionContainer = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  ${glyphs.Icon} {
    margin-top: 1px;
    color: ${({ theme }) => `${(theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500}`};
  }

  &:hover {
    ${glyphs.Icon} {
      color: ${({ theme }) => `${(theme as any)?.colors?.hover ?? colors.PRIMARY_BLUE_700}`};
    }
  }
`

export const SubscriptionText = styled(heading.XXXS)`
  font-size: 14px; // HACK: Irregular font manipulation. See recette

  @media screen and ${breakpoints.MOBILE_L} {
    font-size: 16px; // HACK: Irregular font manipulation. See recette
  }
`
